import type { ServiceGender } from "~/types";

const GENDER_COOKIE = "serviceGender";
const PROMPTED_COOKIE = "serviceGenderPrompted";
const MIXED_SECTIONS_COOKIE = "serviceGenderMixedSections";

export function useServiceGender() {
  const { selectedLocation } = storeToRefs(useBusinessStore());

  const genderCookie = useCookie<Record<string, ServiceGender>>(GENDER_COOKIE, {
    default: () => ({}),
    maxAge: 60 * 60 * 24 * 30,
  });

  const promptedCookie = useCookie<Record<string, boolean>>(PROMPTED_COOKIE, {
    default: () => ({}),
    maxAge: 60 * 60 * 24 * 30,
  });

  const mixedSectionsCookie = useCookie<Record<string, boolean>>(MIXED_SECTIONS_COOKIE, {
    default: () => ({}),
    maxAge: 60 * 60 * 24 * 30,
  });

  const locationKey = computed(() => selectedLocation.value?.identifier ?? "");

  const isMixedMarket = computed(
    () => selectedLocation.value?.targeted_market_key === "ladies_and_gentlemen"
  );

  const hasMixedSections = computed(() => {
    if (typeof selectedLocation.value?.has_mixed_sections === "boolean") {
      return selectedLocation.value.has_mixed_sections;
    }
    return !!(locationKey.value && mixedSectionsCookie.value?.[locationKey.value]);
  });

  const showSectionSelector = computed(
    () => isMixedMarket.value && hasMixedSections.value
  );

  const hasBothGenderEmployees = computed(
    () => !!selectedLocation.value?.has_both_gender_employees
  );

  const selectedGender = computed<ServiceGender | null>(() => {
    if (!isMixedMarket.value) {
      return null;
    }
    const stored = locationKey.value ? genderCookie.value?.[locationKey.value] : null;
    if (stored) {
      return stored;
    }
    if (!hasBothGenderEmployees.value) {
      return "male";
    }
    return null;
  });

  const isSelectorOpen = useState<boolean>("serviceGenderSelectorOpen", () => false);

  const hasPrompted = computed(
    () => !!(locationKey.value && promptedCookie.value?.[locationKey.value])
  );

  const markPrompted = () => {
    if (!locationKey.value) {
      return;
    }
    promptedCookie.value = { ...promptedCookie.value, [locationKey.value]: true };
  };

  const openSelector = () => {
    isSelectorOpen.value = true;
  };

  const closeSelector = () => {
    isSelectorOpen.value = false;
  };

  const setGender = (gender: ServiceGender | null) => {
    if (!locationKey.value) {
      return;
    }

    if (gender) {
      genderCookie.value = { ...genderCookie.value, [locationKey.value]: gender };
    } else {
      genderCookie.value = Object.fromEntries(
        Object.entries(genderCookie.value).filter(([key]) => key !== locationKey.value),
      );
    }
  };

  const genderQuery = computed<Record<string, ServiceGender>>(() =>
    selectedGender.value ? { gender: selectedGender.value } : {}
  );

  const setHasMixedSections = (value: boolean) => {
    if (selectedLocation.value) {
      selectedLocation.value.has_mixed_sections = value;
    }
    if (!locationKey.value) {
      return;
    }
    mixedSectionsCookie.value = {
      ...mixedSectionsCookie.value,
      [locationKey.value]: value,
    };
  };

  return {
    isMixedMarket,
    hasMixedSections,
    showSectionSelector,
    hasBothGenderEmployees,
    selectedGender,
    isSelectorOpen,
    hasPrompted,
    markPrompted,
    openSelector,
    closeSelector,
    setGender,
    genderQuery,
    setHasMixedSections,
  };
}